import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { KnowledgeBase } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { ArrowLeft, Plus, X } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import { quillModules, quillFormats } from '../components/ui/quill-config.js';

export default function NewKnowledge() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [article, setArticle] = useState({
    title: '',
    content: '',
    category: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Categorias disponíveis
  const categories = [
    'Tutoriais',
    'FAQ',
    'Guias',
    'Suporte',
    'Produtos',
    'Geral',
    'Sistemas',
    'Suporte Técnico',
    'Faturamento',
    'Hardware',
    'Software',
    'Redes',
    'Procedimentos'
  ];

  console.log('📋 Categorias disponíveis:', categories);

  const addTag = () => {
    if (newTag.trim() && !article.tags.includes(newTag.trim())) {
      setArticle({
        ...article,
        tags: [...article.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setArticle({
      ...article,
      tags: article.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Enviar os dados do artigo
      const articleData = {
        title: article.title,
        content: article.content,
        category: article.category,
        tags: article.tags
      };
      
      console.log('📝 Criando artigo com dados:', articleData);
      const result = await KnowledgeBase.create(articleData);
      console.log('✅ Artigo criado com sucesso:', result);
      navigate(createPageUrl('Knowledge'));
    } catch (error) {
      console.error('❌ Error creating article:', error);
      alert(`Erro ao criar artigo: ${error.message}`);
    }
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Knowledge'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Novo Artigo</h1>
          <p className="text-muted-foreground mt-1">
            Adicione um novo artigo à base de conhecimento
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  placeholder="Digite o título do artigo"
                  value={article.title}
                  onChange={(e) => setArticle({ ...article, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                
                {/* Teste: Select nativo HTML para verificar se o problema é com o componente Shadcn */}
                <select 
                  value={article.category} 
                  onChange={(e) => {
                    console.log('🔄 Categoria selecionada (select nativo):', e.target.value);
                    setArticle({ ...article, category: e.target.value });
                  }}
                  className="w-full p-2 border border-input bg-background rounded-md"
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((category, index) => {
                    console.log(`📝 Renderizando opção ${index}:`, category);
                    return (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    );
                  })}
                </select>
                
                <p className="text-xs text-muted-foreground">
                  Debug: {categories.length} categorias carregadas | Categoria atual: "{article.category}"
                </p>
                
                {/* Select Shadcn para comparação */}
                <div className="mt-3">
                  <Label className="text-xs text-muted-foreground">Teste Select Shadcn:</Label>
                  <Select 
                    value={article.category} 
                    onValueChange={(value) => {
                      console.log('🔄 Categoria selecionada (Shadcn):', value);
                      setArticle({ ...article, category: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma categoria (Shadcn)" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category, index) => {
                        console.log(`📝 Renderizando categoria Shadcn ${index}:`, category);
                        return (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Input alternativo para categoria personalizada */}
                <div className="mt-2">
                  <Label className="text-xs text-muted-foreground">Ou digite uma categoria personalizada:</Label>
                  <Input
                    placeholder="Digite uma nova categoria"
                    value={article.category}
                    onChange={(e) => setArticle({ ...article, category: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Adicione tags"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTag}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {article.tags.map((tag, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-secondary-foreground/70 hover:text-secondary-foreground"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Conteúdo</Label>
                <div className="min-h-[400px] border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={article.content}
                    onChange={(content) => setArticle({ ...article, content })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="h-[350px]"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Knowledge'))}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : 'Salvar Artigo'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}